// src/components/SideMenu.jsx
//
// Restyled to match CET-HTML-Code/dashboard.html's sidebar (the
// "sidenav-menu" / "side-nav" markup) -- Tabler icons, the theme's own
// collapsible-submenu look, instead of the app's old hand-rolled
// sidebar-* classes and the portal-based Master flyout.
//
// The template drives its Master/Account/Configuration submenus with
// Bootstrap's JS collapse plugin (data-bs-toggle="collapse"). That
// plugin isn't loaded here (see the note in public/index.html) -- this
// uses plain React state instead, toggling the exact same
// ".collapse"/".collapse.show" classes Bootstrap's CSS already
// styles, so the look (including the open/close animation, which is a
// pure CSS transition on max-height) is unchanged.
//
// Link set: Dashboard, Project, Cost Estimation, Rate Card, a Master
// submenu (Role / Department / Company -- the three master-data pages
// that actually exist today), Account, a Configuration submenu, Help,
// Sign out. The template's sidebar shows several more Master entries
// (Asset Element, Asset Group, BOQ Division, Class, Currency, Main
// Element, Operating Company, Sub Element, Unit) -- those aren't
// linked here because those pages don't exist in this app yet (see
// HTML-REDESIGN-PLAN.md's Category B/C/D) -- adding a nav link to a
// page that 404s would be worse than not having the link. They'll be
// added to this same list as each page gets built.
//
// Configuration submenu: Mail Configuration is real (routes to
// EmailConfiguration.jsx, backed by the Mail_Notification_Templates
// table app.py already reads from when it sends real emails).
// Notifications reuses the existing /notifications page -- the theme
// puts a link to it here too, alongside the header bell's own link.
// SMS Configuration has no backend at all yet (no SMS provider is
// wired into this app anywhere), so it's rendered as a plain disabled
// row instead of a NavLink -- same "don't link to a 404" reasoning as
// the unbuilt Master entries above, just inside a submenu instead of
// left out of the list entirely.
import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { isModuleVisible } from "../utils/permissions";
import { clearWizardDraft } from "../utils/wizardDraft";
import { ROUTES } from "../config/routes";

const MASTER_PATHS = [ROUTES.MASTER, ROUTES.ACCOUNT_ROLES, ROUTES.ACCOUNT_DEPARTMENTS, ROUTES.ACCOUNT_COMPANIES];
// Account's own flyout -- Users (existing) + Manage Roles (new, the
// per-user View/Contributor/Admin/Super Admin tier assignment page from
// the Role Matrix doc). Account used to be a single direct NavLink to
// /account/users; it's now a collapsible submenu like Master, above.
const ACCOUNT_PATHS = [ROUTES.ACCOUNT_USERS, ROUTES.ACCOUNT_MANAGE_ROLES, ROUTES.ACCOUNT_ROLE_MATRIX];
// "Notifications" isn't under /configuration/ in the route table (it's a
// top-level /notifications route, reused from the header bell), but it's
// rendered inside this Configuration submenu below -- so it still needs to
// count as "on a Configuration page" for the submenu's own open/active
// state. Also added /configuration/mail/templates/new, which was missing
// here -- being on the "Add Template" screen was falling out of both the
// submenu-stays-open behavior and the Configuration toggle's own active
// state, since it only started with "/configuration/mail" via a stricter
// check before this file's routing helper below was made prefix-aware.
const CONFIGURATION_PATHS = [ROUTES.CONFIG_MAIL, ROUTES.NOTIFICATIONS];

// Route groups for the top-level items below (everything that isn't a
// Master/Configuration submenu toggle). "Cost Estimation" covers the
// whole 7-step wizard plus its offshoot pages, not just step 1 -- being
// on step 4 should still show "Cost Estimation" as the current section.
const COST_ESTIMATION_PATHS = [
  ROUTES.COST_ESTIMATION_STEP1,
  ROUTES.COST_ESTIMATION_STEP2,
  ROUTES.COST_ESTIMATION_STEP3,
  ROUTES.COST_ESTIMATION_STEP4,
  ROUTES.COST_ESTIMATION_STEP5,
  ROUTES.COST_ESTIMATION_SUMMARY,
  ROUTES.BASIS_OF_COST,
  ROUTES.ELEMENT_SCOPE_PAGE1,
];

export default function SideMenu({ sidebarCondensed = false, onToggleSidebar = () => {} }) {
  const location = useLocation();
  const path = location.pathname;

  // Master is now a plain link (see the NavLink below) rather than a
  // collapsible submenu -- no open/close state needed for it anymore,
  // just the active-highlight check.
  const isOnMasterPage = MASTER_PATHS.some((p) => path.startsWith(p));

  const isOnConfigurationPage = CONFIGURATION_PATHS.some((p) => path.startsWith(p));
  const [configOpen, setConfigOpen] = useState(isOnConfigurationPage);

  const isOnAccountPage = ACCOUNT_PATHS.some((p) => path.startsWith(p));
  const [accountOpen, setAccountOpen] = useState(isOnAccountPage);

  // Collapse each submenu automatically if the user navigates away from
  // it some other way (e.g. a "Back" browser action), so it doesn't
  // stay stuck open showing the wrong section as current.
  useEffect(() => {
    if (!CONFIGURATION_PATHS.some((p) => path.startsWith(p))) {
      setConfigOpen(false);
    }
    if (!ACCOUNT_PATHS.some((p) => path.startsWith(p))) {
      setAccountOpen(false);
    }
  }, [path]);

  const linkClass = ({ isActive }) => `side-nav-link${isActive ? " active" : ""}`;

  // ROOT CAUSE of "no item ever shows as highlighted": app.min.css's own
  // current-page styling is written as `.side-nav > .side-nav-item.active
  // > a` / `> button` -- it needs the .active class on the <li> (the
  // .side-nav-item), NOT on the <a>/<button> inside it. NavLink's isActive
  // (via linkClass above) only ever put .active on the <a> itself, so that
  // theme rule never matched anything -- every item looked identically
  // unhighlighted no matter which page you were on. This helper computes
  // it directly from the current path so it can be applied to each <li>
  // below. (What actually looked "highlighted" before this fix -- Master/
  // Configuration turning bright green on hover -- was unrelated: those
  // two are styled as Bootstrap .btn.btn-link so they can act as toggle
  // buttons, and app.min.css's global `.btn:hover { background-color:
  // #2ccf72 }` rule was firing on mouseover. See the :hover override
  // added to themeOverrides.css for that.)
  const itemClass = (active) => `side-nav-item${active ? " active" : ""}`;

  const isDashboardActive = path === ROUTES.DASHBOARD;
  const isProjectActive = path === ROUTES.PROJECT;
  const isCostEstimationActive = COST_ESTIMATION_PATHS.includes(path);
  const isRateCardActive = path === ROUTES.RATE_CARD;
  // startsWith, not ===, so the Create/Edit User sub-pages still count as
  // "on Users" and keep it highlighted within the Account submenu.
  const isUsersActive = path.startsWith(ROUTES.ACCOUNT_USERS);
  const isManageRolesActive = path === ROUTES.ACCOUNT_MANAGE_ROLES;
  const isRoleMatrixActive = path === ROUTES.ACCOUNT_ROLE_MATRIX;
  const isHelpActive = path === ROUTES.HELP;
  const isSignOutActive = path === "/signout";

  const isMailConfigActive = path.startsWith(ROUTES.CONFIG_MAIL);
  const isNotificationsActive = path === ROUTES.NOTIFICATIONS;

  return (
    <div className="sidenav-menu">
      <NavLink to={ROUTES.DASHBOARD} className="logo">
        <span className="logo logo-light">
          {/* Per request, the expanded sidebar (.logo-lg) shows the FULL
              logo (with the "COST ESTIMATION TOOL" wordmark), placed
              directly on the sidebar with no background chip behind it
              (per request -- an earlier white chip version was removed).
              CET-Full-logo-white.png is a recolored copy of the brand
              logo (icon pixels untouched, "COST"/"ESTIMATION TOOL" text
              pixels turned white) -- used here instead of the original
              dark-navy/gray-text CET-Full-logo.png because the plain
              dark text was low-contrast on the sidebar's dark green.
              Login.jsx keeps the original dark-text version since its
              card background is white. Sizing is enforced by the
              !important rule in themeOverrides.css (".logo .logo-lg
              img"), not inline style -- the theme's own CSS otherwise
              wins and renders this oversized. The collapsed (.logo-sm)
              slot keeps the icon-only mark, since the wide wordmark
              can't fit in the ~75px collapsed sidebar width. */}
          <span className="logo-lg">
            <img src="/assets/images/logo/CET-Full-logo-white.png" alt="Cost Estimation Tool" />
          </span>
          <span className="logo-sm">
            <img src="/assets/images/logo/CET-Full-icon.png" alt="Cost Estimation Tool" />
          </span>
        </span>
      </NavLink>

      {/* Sidebar pin/unpin toggle -- this is the theme's own
          .button-on-hover control, straight out of CET-HTML-Code's
          sidenav-menu markup (a sibling of .logo, not nested inside
          it -- matches the recording exactly, including its circular
          ring-with-dot look, its position (top-right corner of the logo
          area), and its behavior. Everything about how it looks and
          behaves is driven by app.min.css off the html[data-sidenav-size]
          value that onToggleSidebar flips (see Layout.jsx) -- the
          filled center dot appearing/disappearing, the sidebar
          expanding on hover when unpinned, all of it is already built
          into the theme's CSS for this exact button+attribute
          combination, nothing custom needed here beyond wiring the
          click. */}
      <button
        type="button"
        className="button-on-hover"
        onClick={onToggleSidebar}
        aria-label={sidebarCondensed ? "Pin sidebar open" : "Unpin sidebar to icons only"}
        title={sidebarCondensed ? "Pin sidebar open" : "Unpin sidebar to icons only"}
      >
        <span className="btn-on-hover-icon" />
      </button>

      <div className="scrollbar">
        <div id="sidenav-menu">
          <ul className="side-nav">
            <li className={itemClass(isDashboardActive)}>
              <NavLink to={ROUTES.DASHBOARD} end className={linkClass}>
                <span className="menu-icon">
                  <i className="ti ti-dashboard" />
                </span>
                <span className="menu-text">Dashboard</span>
              </NavLink>
            </li>

            <li className={itemClass(isProjectActive)}>
              <NavLink to={ROUTES.PROJECT} className={linkClass}>
                <span className="menu-icon">
                  <i className="ti ti-folder" />
                </span>
                <span className="menu-text">Project</span>
              </NavLink>
            </li>

            {/* Cost Estimation (the wizard) and Rate Card are both hidden
                for the View tier per the Role Matrix -- View can look at
                finished estimations via Project, but can't open the
                wizard to create/edit one, and Rate Card is "Not visible"
                to View outright. */}
            {isModuleVisible("CostEstimation") && (
              <li className={itemClass(isCostEstimationActive)}>
                {/* FIX: this always lands on Step 1 regardless of which
                    step (or whose project) was last open, so -- like the
                    Explore dropdown's "Cost Estimation" item and the
                    dashboard promo banner's "Start Estimation" button in
                    Header.jsx -- it's really a "start a NEW estimation"
                    entry point, not a "resume where I left off" link.
                    Without clearing the draft first, Step 1 (and every
                    later step, if the user carries on) kept showing
                    whatever project was last worked on instead of a
                    blank form. See src/utils/wizardDraft.js. */}
                <NavLink to={ROUTES.COST_ESTIMATION_STEP1} className={linkClass} onClick={clearWizardDraft}>
                  <span className="menu-icon">
                    <i className="ti ti-calculator" />
                  </span>
                  <span className="menu-text">Cost Estimation</span>
                </NavLink>
              </li>
            )}

            {isModuleVisible("RateCard") && (
              <li className={itemClass(isRateCardActive)}>
                <NavLink to={ROUTES.RATE_CARD} className={linkClass}>
                  <span className="menu-icon">
                    {/* Was ti-currency-dollar, then the Tabler
                        ti-currency-riyal glyph -- swapped again for the
                        real official Saudi Riyal symbol (the user's own
                        white-on-transparent artwork, cropped/optimized
                        from public/assets/images/logo/ryl.png), since the
                        Tabler icon is only a generic approximation. Sized
                        to match --theme-sidenav-item-icon-size (1.125rem)
                        the same class other menu icons use -- an <img>
                        doesn't pick that up automatically the way the
                        .menu-icon svg/i CSS rules do for font icons. */}
                    <img
                      src="/assets/images/logo/riyal-symbol-icon.png"
                      alt="Rate Card"
                      style={{ height: "1.125rem", width: "auto" }}
                    />
                  </span>
                  <span className="menu-text">Rate Card</span>
                </NavLink>
              </li>
            )}

            {/* AI Cost Estimation MVP -- Suggest Rate / Similar Items over
                the existing Rate_Card data (see AIAssistant.jsx and
                app.py's ai_module_api). Per request, the "AI Assistant"
                sidebar link is hidden -- the route (/ai-assistant) and
                page are left in place, only this nav entry is removed, so
                the feature stays reachable by direct URL if needed later. */}

            {/* Master, Account and Configuration are each hidden entirely
                for tiers the Role Matrix marks "Not visible" for that
                module -- View/Contributor never see Master or Account;
                Admin additionally never sees Configuration (Super Admin
                only). Matches the route guards in App.jsx, which block
                direct URL access the same way. */}
            {/* Per request, Master is now a direct link to the "Coming
                Soon" placeholder page (see App.jsx's "/master" route)
                instead of a toggle that opened an empty submenu -- the
                Role / Department / Company sub-items it used to expand
                are still gone, same as before. */}
            {isModuleVisible("Master") && (
              <li className={itemClass(isOnMasterPage)}>
                <NavLink to={ROUTES.MASTER} className={linkClass}>
                  <span className="menu-icon">
                    <i className="ti ti-database" />
                  </span>
                  <span className="menu-text">Master</span>
                </NavLink>
              </li>
            )}

            {isModuleVisible("AccountUsers") && (
              <li className={itemClass(isOnAccountPage)}>
                <button
                  type="button"
                  className={`side-nav-link btn btn-link text-start w-100 border-0${isOnAccountPage ? " active" : ""}`}
                  onClick={() => setAccountOpen((open) => !open)}
                  aria-expanded={accountOpen}
                >
                  <span className="menu-icon">
                    <i className="ti ti-users" />
                  </span>
                  <span className="menu-text">Account</span>
                  <span className="menu-arrow" />
                </button>
                <div className={`collapse${accountOpen ? " show" : ""}`}>
                  <ul className="sub-menu">
                    <li className={itemClass(isUsersActive)}>
                      <NavLink to={ROUTES.ACCOUNT_USERS} className={linkClass}>
                        <span className="menu-text">Users</span>
                      </NavLink>
                    </li>
                    <li className={itemClass(isManageRolesActive)}>
                      <NavLink to={ROUTES.ACCOUNT_MANAGE_ROLES} className={linkClass}>
                        <span className="menu-text">Manage Roles</span>
                      </NavLink>
                    </li>
                    <li className={itemClass(isRoleMatrixActive)}>
                      <NavLink to={ROUTES.ACCOUNT_ROLE_MATRIX} className={linkClass}>
                        <span className="menu-text">Role Matrix</span>
                      </NavLink>
                    </li>
                  </ul>
                </div>
              </li>
            )}

            {isModuleVisible("Configuration") && (
            <li className={itemClass(isOnConfigurationPage)}>
              <button
                type="button"
                className={`side-nav-link btn btn-link text-start w-100 border-0${isOnConfigurationPage ? " active" : ""}`}
                onClick={() => setConfigOpen((open) => !open)}
                aria-expanded={configOpen}
              >
                <span className="menu-icon">
                  <i className="ti ti-settings" />
                </span>
                <span className="menu-text">Configuration</span>
                <span className="menu-arrow" />
              </button>
              <div className={`collapse${configOpen ? " show" : ""}`}>
                <ul className="sub-menu">
                  <li className={itemClass(isMailConfigActive)}>
                    <NavLink to={ROUTES.CONFIG_MAIL} className={linkClass}>
                      <span className="menu-text">Mail Configuration</span>
                    </NavLink>
                  </li>
                  {/* SMS Configuration removed per request -- no SMS
                      provider is wired into this app anywhere yet (see
                      the file-level comment above), and it's no longer
                      shown here even as a disabled row. */}
                  <li className={itemClass(isNotificationsActive)}>
                    <NavLink to={ROUTES.NOTIFICATIONS} className={linkClass}>
                      <span className="menu-text">Notifications</span>
                    </NavLink>
                  </li>
                </ul>
              </div>
            </li>
            )}

            <li className={itemClass(isHelpActive)}>
              <NavLink to={ROUTES.HELP} className={linkClass}>
                <span className="menu-icon">
                  <i className="ti ti-help" />
                </span>
                <span className="menu-text">Help</span>
              </NavLink>
            </li>

            <li className={itemClass(isSignOutActive)}>
              <NavLink to="/signout" className={linkClass}>
                <span className="menu-icon">
                  <i className="ti ti-logout" />
                </span>
                <span className="menu-text">Sign Out</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}